import onLangChangeObservable from '@/observables/on-lang-change.observable'
import Loader from '@components/loader/loader.component'
import TinyCalendar from '@components/tiny-calendar/tiny-calendar.component'
import StringsHelper from '@helpers/strings.helper'
import { HistoryTransaction } from '@interfaces/payment.interface'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Subscription } from 'rxjs'
import CardPaymentValue from '../card-payment-value/card-payment-value.component'
import InfinityScroll from '../infinity-scroll/infinity-scroll.component'
import './next-payments.component.scoped.scss'
import FinancialOperationService from '../../services/financial-operation/financial-operation.service'

interface Props {
  title?: any
  onPaymentsReceived?: (paymentsReceived: boolean) => void
  onNavigateTo?: (path: string) => void
  showCalendar?: boolean
  showIcon?: boolean
  disableTip?: boolean
}

export default ({ title, onPaymentsReceived, onNavigateTo, showCalendar = true, showIcon = false, disableTip = false }: Props) => {
  const financialService = useMemo(() => new FinancialOperationService(), [])
  const stringsHelper = useMemo(() => new StringsHelper(), [])
  const onLangChangeSubscription = useRef<Subscription>(undefined)
  const [loading, setLoading] = useState(true)
  const [lang, setLang] = useState<string>('en')
  const [loadingList, setLoadingList] = useState(false)
  const [nextPage, setNextPage] = useState<number | null>(1)
  const [transactions, setTransactions] = useState<HistoryTransaction[]>([])
  const { t } = useTranslation('nextPayments')

  const getToReceiveHistory = useCallback(
    (updateList?: boolean) => {
      setLoadingList(true)
      const page = updateList ? 1 : nextPage
      const limit = updateList ? transactions?.length : 10
      financialService
        //@ts-ignore
        .getPaymentHistory('toReceive', page, limit)
        .then((e) => {
          const newArray = updateList ? e?.transactions : transactions.concat(e?.transactions)
          setTransactions(newArray)
          setNextPage(newArray?.length < e?.total ? page + 1 : null)
        })
        .catch(() => {
          setTransactions([])
        })
        .finally(() => {
          setLoadingList(false)
          setLoading(false)
        })
    },
    [nextPage, transactions, financialService]
  )

  const handleNavigate = useCallback(
    (path: string) => {
      if (onNavigateTo && typeof onNavigateTo === 'function') {
        onNavigateTo(path)
      }
    },
    [onNavigateTo]
  )

  useEffect(() => {
    if (!loading && !transactions?.length) {
      if (onPaymentsReceived && typeof onPaymentsReceived === 'function') {
        onPaymentsReceived(false)
      }
    }
  }, [transactions, loading, onPaymentsReceived])

  useEffect(() => {
    getToReceiveHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onLangChangeSubscription.current = onLangChangeObservable.subscribe((lang) => {
      setLang(lang)
    })

    return () => {
      onLangChangeSubscription.current?.unsubscribe()
    }
  }, [])

  if (loading) {
    return (
      <div className="display-flex justify-content-center py-3" style={{ width: '100%' }}>
        <Loader />
      </div>
    )
  }

  return (
    <div className="next-payments mt-5">
      {title ? <h3>{title}</h3> : null}
      {transactions?.length > 0 ? (
        <InfinityScroll
          className="payment-info-page-infinity-scroll"
          loading={loadingList}
          onLoadMore={getToReceiveHistory}
          hasMore={!!nextPage}
          endMessage={' '}
        >
          <div className="next-payments-cards display-flex">
            {transactions?.map((transaction) => (
              <div className="next-payments-cards-container display-flex" key={transaction?.transactionId}>
                {showCalendar ? (
                  <TinyCalendar dateString={transaction?.dueDate} style={{ maxWidth: '38px', width: '100%' }}>
                    <TinyCalendar.Day />
                    <TinyCalendar.Month className="text-big" />
                    <TinyCalendar.Year />
                  </TinyCalendar>
                ) : null}
                <CardPaymentValue
                  key={transaction?.transactionId}
                  paymentObject={transaction}
                  hideValue={false}
                  title={
                    location.pathname === '/home' && showCalendar ? null : (
                      <span>
                        {stringsHelper.capitalizeFirstLetter(new Date(transaction?.dueDate)?.toLocaleString(lang, { month: 'long' }))}{' '}
                        {transaction?.dueDate?.substring(0, 4)}
                      </span>
                    )
                  }
                  showIcon={showIcon}
                  onNavigateTo={handleNavigate}
                  disableTip={disableTip}
                  updateList={() => getToReceiveHistory(true)}
                />
              </div>
            ))}
          </div>
        </InfinityScroll>
      ) : (
        <div className="no-payments">
          <i className="fa-solid fa-sack-dollar" style={{ color: 'var(--gray)', fontSize: '2.5rem' }}></i>
          <p>{t('noPayments')}</p>
        </div>
      )}
    </div>
  )
}
